import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  formatAnalyticsReferenceDateCompact,
  getAnalyticsReferenceDateParams,
  useAnalyticsReferenceDate,
} from '../contexts/AnalyticsReferenceDateContext';
import TimerCard, { Timer } from '../components/TimerCard';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { formatStreak, streakUnitNoun } from '../metrics/streakFormat';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Habit {
  id: number;
  target_count: number;
  is_active: boolean;
  // Populated by GET /api/habits/?include_today=true (see habits router).
  today_completed_count?: number;
  is_due_now?: boolean;
  is_snoozed?: boolean;
}

interface Timeline {
  id: number;
  status: 'planning' | 'active' | 'paused' | 'completed' | 'cancelled';
}

interface TimeseriesPoint {
  period: string;
  completed_count: number;
  target_count: number;
  completion_rate: number;
}

interface HabitOverviewStat {
  habit_id: number;
  habit_title: string;
  completion_rate_30d: number;
  current_streak: number;
  recurrence_type?: string | null;
  recurrence_interval?: number;
  // Adherence over the habit's own recently-closed periods — what best/worst
  // are ranked by. completion_rate_30d is not comparable across recurrences.
  ranking_rate?: number | null;
  ranking_closed_periods?: number;
  rankable?: boolean;
  // Additive ranking-rework fields: closed-cycle progress toward the ranking
  // minimum, and whether this recurrence can ever close enough cycles inside
  // the 1-year lookback window at all (a yearly habit cannot).
  closed_periods?: number;
  required_periods?: number;
  can_ever_rank?: boolean;
}

interface HabitOverview {
  total_habits: number;
  active_habits: number;
  overall_completion_rate_7d: number;
  overall_completion_rate_30d: number;
  best_habit: HabitOverviewStat | null;
  worst_habit: HabitOverviewStat | null;
  unranked_habits?: HabitOverviewStat[];
  ranking_min_periods?: number;
  ranking_lookback_days?: number;
  daily_completions_7d: TimeseriesPoint[];
}

// The slice of GET /api/habits/review the dashboard consumes: just enough to
// surface the single most-overdue habit as a link into the review page.
interface HabitReviewOverdueItem {
  habit_id: number;
  habit_title: string;
  is_overdue?: boolean;
  days_past_due?: number | null;
}

const Dashboard: React.FC = () => {
  const { cardContentPaddingSx, pageGridSpacing, pageHeaderSx, panelPaddingSx } =
    useLayoutTokens();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { asOfDate, enabled: analyticsReferenceDateEnabled } = useAnalyticsReferenceDate();
  const [timers, setTimers] = useState<Timer[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [timelines, setTimelines] = useState<Timeline[]>([]);
  const [habitOverview, setHabitOverview] = useState<HabitOverview | null>(null);
  const [topOverdue, setTopOverdue] = useState<HabitReviewOverdueItem | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setDashboardLoading(true);
        const tzOffset = new Date().getTimezoneOffset();
        const analyticsReferenceDateParams = getAnalyticsReferenceDateParams(
          analyticsReferenceDateEnabled,
          asOfDate,
        );

        // ?include_today=true makes the habits endpoint do the
        // "completed today" / "due now" / "snoozed" math server-side, so
        // we no longer fan out N+1 log fetches per habit here.
        const [timersRes, habitsRes, timelinesRes, overviewRes, reviewRes] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/api/timers/?active_only=true`),
            axios.get(
              `${API_BASE_URL}/api/habits/?include_today=true&tz_offset=${tzOffset}`,
            ),
            axios.get(`${API_BASE_URL}/api/timelines/`),
            axios.get(`${API_BASE_URL}/api/habits/stats/overview`, {
              params: { tz_offset: tzOffset, ...analyticsReferenceDateParams },
            }),
            axios.get(`${API_BASE_URL}/api/habits/review`, {
              params: { tz_offset: tzOffset, ...analyticsReferenceDateParams },
            }),
          ]);

        setTimers(timersRes.data.slice(0, 3));
        setHabits(habitsRes.data);
        setTimelines(timelinesRes.data);
        setHabitOverview(overviewRes.data);
        const reviewItems: HabitReviewOverdueItem[] = reviewRes.data?.items ?? [];
        const overdueItems = reviewItems.filter((item) => item.is_overdue);
        setTopOverdue(
          overdueItems.length > 0
            ? overdueItems.reduce((top, item) =>
                (item.days_past_due ?? 0) > (top.days_past_due ?? 0) ? item : top,
              )
            : null,
        );
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboardData();
  }, [analyticsReferenceDateEnabled, asOfDate]);

  const activeHabits = habits.filter((habit) => habit.is_active);
  const activeTimelines = timelines.filter((timeline) => timeline.status === 'active');
  // "Due now" already excludes snoozed habits server-side, but we filter
  // again defensively in case some caller passed include_today=false.
  const dueHabits = activeHabits.filter((h) => h.is_due_now && !h.is_snoozed);
  const completedHabitsToday = activeHabits.filter(
    (habit) => (habit.today_completed_count ?? 0) >= habit.target_count,
  ).length;

  const completionRate = activeHabits.length
    ? Math.round((completedHabitsToday / activeHabits.length) * 100)
    : 0;

  const unrankedHabits = habitOverview?.unranked_habits ?? [];
  const rankingMinPeriods = habitOverview?.ranking_min_periods || 6;

  if (dashboardLoading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box sx={pageHeaderSx}>
        <Typography variant="h3" component="h1" gutterBottom>
          Welcome back, {user?.full_name || user?.username}!
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Track your habits and long-term goals with Kalpa Niyama
        </Typography>
      </Box>

      <Grid container spacing={pageGridSpacing}>
        {/* Habits Overview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h5" component="h2" gutterBottom>
                Today's Habits
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track your daily habits and build consistency
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h3" color="primary">
                  {completedHabitsToday}/{activeHabits.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Habits completed today
                  {dueHabits.length > 0 && (
                    <> · {dueHabits.length} due now</>
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Timelines Overview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h5" component="h2" gutterBottom>
                Active Timelines
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor your long-term projects and goals
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h3" color="secondary">
                  {activeTimelines.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active timelines
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12}>
          <Paper sx={panelPaddingSx}>
            <Typography variant="h5" component="h2" gutterBottom>
              Quick Stats
            </Typography>
            <Grid container spacing={pageGridSpacing}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {habits.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Habits
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h4" color="secondary">
                    {completionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Completion
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {dueHabits.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Due Now
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Habit Analytics */}
        {habitOverview && habitOverview.active_habits > 0 && (
          <Grid item xs={12}>
            <Paper sx={panelPaddingSx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
                <Typography variant="h5" component="h2">
                  Habit Analytics
                </Typography>
                {analyticsReferenceDateEnabled && asOfDate && (
                  <Chip
                    label={`Analytics as of ${formatAnalyticsReferenceDateCompact(asOfDate)}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              <Grid container spacing={pageGridSpacing} alignItems="center">
                {/* 7-day sparkline */}
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last 7 Days
                  </Typography>
                  <Box sx={{ width: '100%', height: 60 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={habitOverview.daily_completions_7d}>
                        <XAxis dataKey="period" hide />
                        <ReTooltip
                          formatter={(value: number) => [`${value}%`, 'Rate']}
                          labelFormatter={(label: string) => label}
                        />
                        <Line
                          type="monotone"
                          dataKey="completion_rate"
                          stroke="#1976d2"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                {/* Completion rates */}
                <Grid item xs={6} sm={3} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {habitOverview.overall_completion_rate_7d}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      7-day Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {habitOverview.overall_completion_rate_30d}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      30-day Rate
                    </Typography>
                  </Box>
                </Grid>
                {/* Review shortcuts: top overdue / weakest / strongest, each
                    row a pre-filtered link into the Habit Review page. */}
                {(topOverdue || habitOverview.worst_habit || habitOverview.best_habit) && (
                  <Grid item xs={12} sm={6} md={5}>
                    {topOverdue && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate('/habits/review?tab=overdue')}
                        sx={{ cursor: 'pointer', mb: 1 }}
                      >
                        <EventBusyIcon color="error" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {topOverdue.habit_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Overdue · {topOverdue.days_past_due} days past due
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {habitOverview.worst_habit &&
                      habitOverview.worst_habit.habit_id !==
                        habitOverview.best_habit?.habit_id && (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            navigate('/habits/review?tab=adherence&dir=weakest')
                          }
                          sx={{ cursor: 'pointer', mb: 1 }}
                        >
                          <WarningAmberIcon color="warning" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {habitOverview.worst_habit.habit_title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Needs attention ·{' '}
                              {/* Rankable ⇒ ranking_rate is non-null (backend
                                  guarantee). Never fall back to the
                                  cross-recurrence-incomparable 30-day rate. */}
                              {habitOverview.worst_habit.ranking_rate != null
                                ? `${habitOverview.worst_habit.ranking_rate}%`
                                : '—'}
                              {habitOverview.worst_habit.ranking_closed_periods
                                ? ` over ${habitOverview.worst_habit.ranking_closed_periods} ${streakUnitNoun(
                                    habitOverview.worst_habit.recurrence_type,
                                    habitOverview.worst_habit.recurrence_interval ?? 1,
                                    habitOverview.worst_habit.ranking_closed_periods,
                                  )}`
                                : ''}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    {habitOverview.best_habit && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          navigate('/habits/review?tab=adherence&dir=strongest')
                        }
                        sx={{ cursor: 'pointer' }}
                      >
                        <WhatshotIcon color="error" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {habitOverview.best_habit.habit_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Best streak ·{' '}
                            {formatStreak(
                              habitOverview.best_habit.current_streak,
                              habitOverview.best_habit.recurrence_type,
                              habitOverview.best_habit.recurrence_interval ?? 1,
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                )}
                {/* Habits without enough closed cycles to rank. A week-old
                    habit is unmeasured, not the worst performer — and some
                    recurrences can never close enough cycles inside the
                    1-year ranking window at all, so "yet" would be a lie. */}
                {unrankedHabits.length > 0 && (
                  <Grid item xs={12} sm={6} md={5}>
                    {!habitOverview.best_habit &&
                      !habitOverview.worst_habit &&
                      unrankedHabits.some((h) => h.can_ever_rank !== false) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontWeight: 600 }}
                        >
                          Not enough history to rank habits yet.
                        </Typography>
                      )}
                    {unrankedHabits.map((habit) => (
                      <Typography
                        key={habit.habit_id}
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {habit.habit_title} ·{' '}
                        {habit.can_ever_rank === false
                          ? `cycle too long to rank — fewer than ${
                              habit.required_periods || rankingMinPeriods
                            } cycles fit in a year`
                          : `${habit.closed_periods ?? habit.ranking_closed_periods ?? 0} of ${
                              habit.required_periods || rankingMinPeriods
                            } cycles closed`}
                      </Typography>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Countdowns Widget */}
        <Grid item xs={12}>
          <Paper sx={panelPaddingSx}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <TimerIcon color="primary" />
                <Typography variant="h5" component="h2">
                  Countdowns
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/timers')}
              >
                View All
              </Button>
            </Box>
            {dashboardLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : timers.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No active timers yet
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/timers')}
                  sx={{ mt: 1 }}
                >
                  Create Your First Timer
                </Button>
              </Box>
            ) : (
              <Box>
                {timers.map((timer) => (
                  <TimerCard key={timer.id} timer={timer} compact />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default Dashboard;
